import { getMultiplierIndex, MULTIPLIER_WHEEL_VALUE } from '../constants'
import type { SlotResult, SlotResultView } from './SlotResult'

/** 單條連線的判定結果。 */
export interface LineWinInfo {
  readonly lineIndex: number
  /** 中獎符號 ID；-1 表示該線未達門檻。 */
  readonly symbol: number
  /**
   * 相鄰相符數（= 由左連續相符的長度 − 1）。
   * 對應 Unity 儲存於 MGLineCount 的值；連續符號數 = adjacentMatches + 1。
   */
  readonly adjacentMatches: number
}

/**
 * 連線判定 —— 忠實移植 Unity Slot001_SlotResult.CheckWin。
 * 僅供「顯示」用（獎金以伺服器 mgLineWin 為準）。
 *
 * @param board 盤面 [reel][row]
 * @param lines 連線定義，每條長度 = 轉輪數，元素為該輪要取的 row index
 * @param wildIndex Wild 符號索引
 * @param threshold 門檻（相鄰相符數 ≥ threshold 才算中；來源為 2）
 */
export function checkLines(
  board: ReadonlyArray<ReadonlyArray<number>>,
  lines: ReadonlyArray<ReadonlyArray<number>>,
  wildIndex: number,
  threshold: number,
): LineWinInfo[] {
  const results: LineWinInfo[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    let count = 0
    let lineSymbol = -1

    for (let j = 0; j < line.length - 1; j++) {
      const current = board[j][line[j]]
      const next = board[j + 1][line[j + 1]]

      // 取第一個非 Wild 符號作為比對基準
      if (lineSymbol === -1) {
        if (current !== wildIndex) lineSymbol = current
        else if (next !== wildIndex) lineSymbol = next
      }

      if (lineSymbol === next || next === wildIndex) {
        count++
      } else {
        break
      }
    }

    const win = count >= threshold
    results.push({
      lineIndex: i,
      symbol: win ? lineSymbol : -1,
      adjacentMatches: win ? count : 0,
    })
  }

  return results
}

/**
 * 由伺服器結果計算客戶端衍生欄位 —— 對應 Unity Slot001_SlotResult.UpdateWin。
 * @param toClientBalance 金額轉換器（見 framework/net/balance.ts）
 */
export function buildResultView(
  result: SlotResult,
  toClientBalance: (raw: bigint) => number,
): SlotResultView {
  const bWin = result.mgResult.mainWin > 0n

  let totalLineWin = 0
  if (bWin) {
    let total = 0n
    for (const lineWin of result.mgResult.mgLineWin) {
      total += lineWin
    }
    totalLineWin = toClientBalance(total)
  }

  // Bonus 轉盤演出觸發條件 —— 對應 Unity Slot001_MultiplierReels.CheckWin：
  // 唯有倍數轉輪「停在 Wheel 符號」（mgMultiplier === 0）且伺服器回傳 bonusWin > 0 時才進場。
  // 僅靠 bonusWin > 0 不足以觸發（避免每局誤觸發轉輪演出）。
  const bBonus =
    result.mgResult.mgMultiplier === MULTIPLIER_WHEEL_VALUE && result.bgResult.bonusWin > 0n

  return {
    result,
    bWin,
    bBonus,
    totalLineWin,
    dBonusWin: toClientBalance(result.bgResult.bonusWin),
    mgMultiplierIndex: getMultiplierIndex(result.mgResult.mgMultiplier),
  }
}
